import { useState, type ReactNode } from 'react';
import type { MessageDetail } from '@/api';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import {
  cachedMailboxEmail,
  mailboxFailureSentence,
  refreshAfterMailboxFailure,
  useOptionalQueryClient,
} from '@/features/dav/mailboxFailure';
import { openableURL } from '@/features/message-frame/frameDocument';
import './unsubscribe.css';

/**
 * "Unsubscribe" beside the sender (RFC 2369 / RFC 8058).
 *
 * The server has already picked one route out of the message's
 * `List-Unsubscribe` and sanitized it; this decides only how to offer it, and
 * the rule for all three is the same: **nothing happens without a second,
 * explicit yes**, and nothing is ever sent in the user's name.
 *
 * - `one_click` — an inline confirm naming the host, then `POST
 *   …/unsubscribe`. The SERVER makes the RFC 8058 request; this page never
 *   touches the sender's URL.
 * - `mailto` — a prefilled composer the user sends (or doesn't) themselves.
 *   Offered only where there is a composer to open (`onCompose`); the main
 *   view and the message pop-out window each open their own.
 * - `link` — the URL is shown first, then opened the way every other link in
 *   a message is (ADR 0052): `openableURL`, so http(s) only, and no opener, no
 *   Referer.
 *
 * ## Not in Junk
 *
 * An unsubscribe link in spam is the classic way to confirm to a spammer that
 * an address is read. So in Junk the control is absent — not disabled — and a
 * short note says why and what to do instead.
 *
 * Every piece of state carries the id of the message it is about, so opening
 * the next message starts clean with no effect (the reading pane's pattern).
 */

/** What the composer is seeded with for a `mailto` unsubscribe. */
export interface UnsubscribeComposeSeed {
  to: string;
  subject: string;
  body: string;
}

type Phase =
  | { kind: 'confirm' }
  | { kind: 'sending' }
  | { kind: 'done'; host: string }
  | { kind: 'failed'; text: string };

export interface UnsubscribeOptions {
  message: MessageDetail | undefined;
  accountId: string | undefined;
  isJunk: boolean;
  onCompose?: ((seed: UnsubscribeComposeSeed) => void) | undefined;
}

export interface UnsubscribeParts {
  /** The button, for beside the sender. `null` when nothing is offered. */
  trigger: ReactNode;
  /** The confirm, the result, or the Junk note, for under the header. */
  panel: ReactNode;
}

export const JUNK_UNSUBSCRIBE_NOTE =
  'Unsubscribe isn’t offered in Junk: unsubscribe links in spam can confirm your address to the sender. Mark it Not spam first if you trust it.';

export function useUnsubscribe({
  message,
  accountId,
  isJunk,
  onCompose,
}: UnsubscribeOptions): UnsubscribeParts {
  const api = useApi();
  const queryClient = useOptionalQueryClient();
  const [state, setState] = useState<{ messageId: string; phase: Phase } | undefined>(undefined);

  const route = message?.unsubscribe;
  if (!message || !route) return { trigger: null, panel: null };

  if (isJunk) {
    return {
      trigger: null,
      panel: <p className="reading-unsubscribe reading-unsubscribe--note muted">{JUNK_UNSUBSCRIBE_NOTE}</p>,
    };
  }

  const account = accountId !== undefined && accountId !== '' ? accountId : message.accountId;
  const link = route.method === 'link' ? openableURL(route.target) : null;
  const oneClickHost = route.method === 'one_click' ? hostOf(route.target) : undefined;

  // Offered only where it can be kept: no composer, no mailto; a link that is
  // not http(s), no link; a one-click target that is not a URL, nothing.
  const offered =
    (route.method === 'mailto' && onCompose !== undefined && route.target !== '') ||
    (route.method === 'link' && link !== null) ||
    (route.method === 'one_click' && oneClickHost !== undefined);
  if (!offered) return { trigger: null, panel: null };

  const phase = state?.messageId === message.id ? state.phase : undefined;
  const set = (next: Phase | undefined) =>
    setState(next === undefined ? undefined : { messageId: message.id, phase: next });

  const start = () => {
    if (route.method === 'mailto') {
      // The composer IS the confirmation: it opens filled in and waits for
      // the user to press Send, or to close it.
      onCompose?.({ to: route.target, subject: route.subject ?? '', body: route.body ?? '' });
      return;
    }
    set({ kind: 'confirm' });
  };

  const confirmOneClick = () => {
    set({ kind: 'sending' });
    api.messages.unsubscribe(account, message.id).then(
      (result) => set({ kind: 'done', host: result.host }),
      (error: unknown) => {
        refreshAfterMailboxFailure(queryClient, error);
        set({
          kind: 'failed',
          text:
            mailboxFailureSentence(error, cachedMailboxEmail(queryClient, account), 'nothing was sent') ??
            failureText(error),
        });
      },
    );
  };

  const openLink = () => {
    if (link === null) return;
    window.open(link.href, '_blank', 'noopener,noreferrer');
    set(undefined);
  };

  const done = phase?.kind === 'done';
  const trigger = done ? null : (
    <button
      type="button"
      className="btn btn--ghost reading-unsubscribe__trigger"
      aria-expanded={route.method === 'mailto' ? undefined : phase !== undefined}
      disabled={phase?.kind === 'sending'}
      onClick={start}
    >
      Unsubscribe
    </button>
  );

  let panel: ReactNode = null;
  if (phase?.kind === 'done') {
    panel = (
      <p className="reading-unsubscribe reading-unsubscribe--done" role="status">
        Unsubscribed
      </p>
    );
  } else if (phase?.kind === 'failed') {
    panel = (
      <p className="reading-unsubscribe reading-unsubscribe--failed" role="alert">
        Unsubscribe failed: {phase.text}
      </p>
    );
  } else if (phase !== undefined && route.method === 'one_click') {
    panel = (
      <div className="reading-unsubscribe" role="group" aria-label="Unsubscribe">
        <p className="reading-unsubscribe__text">
          Unsubscribe from this mailing list? We’ll ask {oneClickHost} to stop sending.
        </p>
        <div className="reading-unsubscribe__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={phase.kind === 'sending'}
            onClick={confirmOneClick}
          >
            {phase.kind === 'sending' ? 'Unsubscribing…' : 'Unsubscribe'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={phase.kind === 'sending'}
            onClick={() => set(undefined)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  } else if (phase !== undefined && link !== null) {
    panel = (
      <div className="reading-unsubscribe" role="group" aria-label="Unsubscribe">
        <p className="reading-unsubscribe__text">
          Opens <span className="reading-unsubscribe__url">{link.href}</span> in a new tab. The
          sender’s page does the rest.
        </p>
        <div className="reading-unsubscribe__actions">
          <button type="button" className="btn btn--primary" onClick={openLink}>
            Open
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => set(undefined)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return { trigger, panel };
}

function hostOf(target: string): string | undefined {
  try {
    const url = new URL(target);
    return url.protocol === 'https:' && url.host !== '' ? url.host : undefined;
  } catch {
    return undefined;
  }
}

/** The server's sentence, which names the host and what went wrong. */
function failureText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isOffline) return 'you’re offline.';
    if (error.message !== '') return error.message;
  }
  if (error instanceof Error && error.message !== '') return error.message;
  return 'the list didn’t answer.';
}
